import { notifications } from '@mantine/notifications';
import { NOTIFICATION_VARIANTS } from 'constants/chownow';
import { MENU_NOTIFICATION_EVENT, MENU_NOTIFICATION_TYPE } from 'constants/menuManagement';
import { formatNotificationTimestamp } from 'helpers/format';

const NOTIFICATION_DELAY = 300;

interface SuccessNotificationParams {
  readableItemType?: string;
  parentUuid?: string;
  extraProps?: Record<string, unknown>;
  customMessage?: string;
  autoClose?: number | false;
  withCloseButton?: boolean;
}

interface ErrorNotificationParams {
  readableItemType?: string;
  customMessage?: string;
  autoClose?: number | false;
  withCloseButton?: boolean;
}

function showDelayedNotification(
  message: string,
  variant: keyof typeof NOTIFICATION_VARIANTS,
  autoClose?: number | false,
  withCloseButton?: boolean,
  delay = NOTIFICATION_DELAY,
) {
  setTimeout(() => {
    const notificationVariant = NOTIFICATION_VARIANTS[variant];
    notifications.show({
      message,
      variant: notificationVariant.label,
      icon: notificationVariant.icon,
      style: { marginTop: 'var(--mantine-spacing-sm)' },
      ...(autoClose !== undefined && { autoClose }),
      // The app-wide Notification theme default is withCloseButton: false
      // (most notifications auto-close in 2s) - override it for callers
      // that request a longer-lived, manually-dismissible notification.
      ...(withCloseButton !== undefined && { withCloseButton }),
    });
  }, delay);
}

export function useHandleSuccessNotification() {
  return ({
    readableItemType,
    parentUuid,
    extraProps = {},
    customMessage,
    autoClose,
    withCloseButton,
  }: SuccessNotificationParams) => {
    const event = new CustomEvent(MENU_NOTIFICATION_EVENT, {
      detail: {
        type: MENU_NOTIFICATION_TYPE.SUCCESS,
        id: extraProps?.id,
        parentUuid,
        ...extraProps,
      },
    });
    window.dispatchEvent(event);

    const timestamp = formatNotificationTimestamp();
    const defaultMessage = readableItemType
      ? `You have successfully updated your ${readableItemType} on ${timestamp}.`
      : `Update successful on ${timestamp}.`;

    showDelayedNotification(customMessage || defaultMessage, 'success', autoClose, withCloseButton);
  };
}

export function useHandleErrorNotification() {
  return ({
    readableItemType,
    customMessage,
    autoClose,
    withCloseButton,
  }: ErrorNotificationParams) => {
    const defaultMessage = readableItemType
      ? `We could not update your ${readableItemType}. Please contact customer support`
      : 'We could not complete the update. Please contact customer support';

    showDelayedNotification(customMessage || defaultMessage, 'caution', autoClose, withCloseButton);
  };
}
